import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CreditPortfolioTable = ({ credits, onViewDetails, onCreateClaim }) => {
  const [sortField, setSortField] = useState('serialNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCredits = [...credits]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      'CCP-Approved': { color: 'bg-success text-success-foreground', icon: 'CheckCircle' },
      'Under Review': { color: 'bg-warning text-warning-foreground', icon: 'Clock' },
      'Non-Compliant': { color: 'bg-error text-error-foreground', icon: 'AlertCircle' },
      'Pending': { color: 'bg-muted text-muted-foreground', icon: 'Circle' }
    };

    const config = statusConfig?.[status] || statusConfig?.['Pending'];
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{status}</span>
      </span>
    );
  };

  const getIntegrityScore = (score) => {
    if (score >= 90) return { color: 'text-success', label: 'High' };
    if (score >= 70) return { color: 'text-warning', label: 'Medium' };
    return { color: 'text-error', label: 'Low' };
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-card-foreground">Credit Portfolio</h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" iconName="Filter" iconPosition="left">
              Filter
            </Button>
            <Button variant="outline" iconName="Download" iconPosition="left">
              Export
            </Button>
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {[
                { key: 'serialNumber', label: 'Serial Number' },
                { key: 'registry', label: 'Registry' },
                { key: 'methodology', label: 'Methodology' },
                { key: 'vintage', label: 'Vintage' },
                { key: 'quantity', label: 'Quantity' },
                { key: 'ccpStatus', label: 'CCP Status' },
                { key: 'integrityScore', label: 'Integrity' },
                { key: 'actions', label: 'Actions' }
              ]?.map((column) => (
                <th 
                  key={column?.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${
                    column?.key !== 'actions' ? 'cursor-pointer hover:text-card-foreground' : ''
                  }`}
                  onClick={() => column?.key !== 'actions' && handleSort(column?.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column?.label}</span>
                    {column?.key !== 'actions' && sortField === column?.key && (
                      <Icon 
                        name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                        size={14} 
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedCredits?.map((credit) => {
              const integrity = getIntegrityScore(credit?.integrityScore);
              return (
                <tr key={credit?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-card-foreground">
                      {credit?.serialNumber}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {credit?.projectName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">
                          {credit?.registry?.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm text-card-foreground">{credit?.registry}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-card-foreground">{credit?.methodology}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-card-foreground">{credit?.vintage}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-card-foreground">
                      {credit?.quantity?.toLocaleString()} tCO2e
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(credit?.ccpStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        integrity?.color === 'text-success' ? 'bg-success' :
                        integrity?.color === 'text-warning' ? 'bg-warning' : 'bg-error'
                      }`} />
                      <span className={`text-sm font-medium ${integrity?.color}`}>
                        {credit?.integrityScore}%
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({integrity?.label})
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        iconName="Eye"
                        onClick={() => onViewDetails(credit)}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        iconName="FileText"
                        onClick={() => onCreateClaim(credit)}
                      >
                        Claim
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {sortedCredits?.map((credit) => {
          const integrity = getIntegrityScore(credit?.integrityScore);
          return (
            <div key={credit?.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium text-card-foreground">
                    {credit?.serialNumber}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {credit?.projectName}
                  </div>
                </div>
                {getStatusBadge(credit?.ccpStatus)}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Registry:</span>
                  <div className="font-medium text-card-foreground">{credit?.registry}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Vintage:</span>
                  <div className="font-medium text-card-foreground">{credit?.vintage}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Quantity:</span>
                  <div className="font-medium text-card-foreground">
                    {credit?.quantity?.toLocaleString()} tCO2e
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Integrity:</span>
                  <div className={`font-medium ${integrity?.color}`}>
                    {credit?.integrityScore}% ({integrity?.label})
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  iconName="Eye"
                  onClick={() => onViewDetails(credit)}
                >
                  View Details
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  iconName="FileText"
                  onClick={() => onCreateClaim(credit)}
                >
                  Create Claim
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CreditPortfolioTable;